import { logger } from "@platform/browser-logger";
import { ColDef, ColGroupDef } from "ag-grid-community";

import { getBenefitDefinitions } from "../../../benefits";

import { buildDataRows } from "./buildRowData";

import type { BenefitDefinition } from "../../../benefits/benefit.types";
import type { LocCoverageType } from "../../../locs/locMap";
import type { BenefitClass } from "../../../types/rfp.api.types";
import type { BenefitPlanGroup, RfpCoverage } from "../../../api/types";

// Performance optimizations: Cache and external functions
export const benefitDefinitionsCache = new Map<
  string,
  Record<string, BenefitDefinition>
>();

/**
 * Builds a Map of planId → eligibilityClasses from BPG data,
 * used to merge eligibility classes into coverage plans.
 */
export const buildEligibilityLookup = (
  benefitPlanGroups: BenefitPlanGroup[] | undefined,
): Map<string, BenefitClass[]> => {
  const lookup = new Map<string, BenefitClass[]>();
  if (!benefitPlanGroups) return lookup;
  for (const group of benefitPlanGroups) {
    for (const bp of group.benefitPlans) {
      if (bp.eligibilityClasses && bp.eligibilityClasses.length > 0) {
        lookup.set(bp.id, bp.eligibilityClasses);
      }
    }
  }
  return lookup;
};

export const buildRowDataFromDependencies = (
  preparedCoverage: RfpCoverage,
  benefitDefinitions: Record<string, BenefitDefinition>,
  columnDefs: (ColDef | ColGroupDef)[],
  hideCurrentCarrierRow = false,
  visibleMpConfigKeys?: ReadonlySet<string>,
): unknown[] => {
  if (
    !preparedCoverage ||
    !benefitDefinitions ||
    Object.keys(benefitDefinitions).length === 0 ||
    columnDefs.length === 0
  ) {
    return [];
  }

  try {
    const builtRowData = buildDataRows(
      preparedCoverage,
      benefitDefinitions,
      columnDefs,
      hideCurrentCarrierRow,
      visibleMpConfigKeys,
    );
    return builtRowData;
  } catch (error) {
    logger.error("Error building row data:", error as Error);
    return [];
  }
};

export const loadBenefitDefinitionsAsync = async (
  coverageType: string,
): Promise<Record<string, BenefitDefinition> | undefined> => {
  if (benefitDefinitionsCache.has(coverageType)) {
    return benefitDefinitionsCache.get(coverageType);
  }

  try {
    const benDefs = await getBenefitDefinitions(
      coverageType as LocCoverageType,
    );

    benefitDefinitionsCache.set(coverageType, benDefs);
    return benDefs;
  } catch (error) {
    logger.error("Error loading benefit definitions:", error as Error);
    return {};
  }
};
